// Envia o lead capturado pela Digi para o ms-landingpage — mesmo fluxo/contrato
// das outras LPs (ex.: Bradesco): POST /api/v1/data com Authorization = token da
// LP (escopado, NAO e master key). O ms-landingpage enfileira e um consumer cria
// o ticket na fase do workflow de forma assincrona.
//
// IMPORTANTE (resiliência a mudança de schema do CRM):
// O banco de CRM é configurável pelo cliente — colunas podem ser adicionadas/
// removidas a qualquer momento (ex.: 29/09/2026 removeram contato.nome_do_contato
// e cliente.banco, o que quebrou register_customer/register_contact e fez leads
// chegarem SEM dados de cliente, silenciosamente). A `add_note` NÃO depende do
// schema do CRM (é texto no ticket) e sobrevive a essas quebras. Por isso a nota
// SEMPRE carrega TODOS os dados informados pelo cliente: mesmo que o cadastro
// estruturado falhe lá no consumer, nada se perde — fica visível no ticket.
//
// Config vem das envs REACT_APP_* (injetadas no build pelo CI). Sem elas (dev
// local), o envio e um no-op e a Digi segue como demonstracao.
import { getJourney, resetJourney } from './journey';

const API_PATH = process.env.REACT_APP_API_PATH;
const TOKEN = process.env.REACT_APP_TOKEN;
const ID_PHASE = process.env.REACT_APP_ID_PHASE;
// Template "Cliente" do tenant (obrigatorio p/ o ms-landingpage criar+vincular o
// cliente). Fallback = template do suporte; pode ser sobrescrito por env no CI.
const ID_TABLE = process.env.REACT_APP_ID_TABLE || '0c837293-a6e5-4396-9564-7b155bf5bb34';
// Fase "Solicitou ambiente" do workflow Inbound (workspace Comercial) — destino
// das solicitacoes de teste/ambiente. Sobrescrevivel por env no CI.
const ID_PHASE_TRIAL = process.env.REACT_APP_ID_PHASE_TRIAL || '13d52c12-f228-42da-b212-ffc484d30148';

export function isLeadEnabled() {
  return Boolean(API_PATH && TOKEN && ID_PHASE);
}

export function isTrialEnabled() {
  return Boolean(API_PATH && TOKEN);
}

// Sinaliza falha de envio no dataLayer (GTM/GA4) para monitoria — como o gateway
// responde de forma assíncrona, este é o único erro que o frontend consegue ver
// (rede / HTTP != 2xx). Erros do cadastro estruturado (cliente/contato) acontecem
// depois, no consumer, e por isso os dados vão íntegros na nota (ver acima).
function alertSendError(flow, status, detail) {
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'lead_send_error', flow, status, detail: detail || '' });
  } catch (_) { /* dataLayer indisponível: ignora */ }
}

// Jornada do visitante do produto (dk.js, carregado em public/*.html): o aceite é o
// próprio envio — o texto "Ao enviar, você concorda…" fica acima do botão (Digi.js).
// Não espera nem bloqueia o lead: a Digi segue na mesma página.
function identifyVisitor(email, telefone) {
  try {
    if (window.dk && window.dk.identify) window.dk.identify({ email, telefone, consentimento: true });
  } catch (_) { /* dk.js fora do ar: o lead segue */ }
}

async function postLead(body, flow) {
  if (!API_PATH || !TOKEN) return { skipped: true };
  try {
    const res = await fetch(new URL(API_PATH), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: TOKEN },
      body: JSON.stringify(body),
    });
    const out = await res.json().catch(() => ({}));
    if (!res.ok) {
      alertSendError(flow, res.status, (out && (out.error || out.message)) || '');
      return { ok: false, status: res.status, ...out };
    }
    // Jornada entregue junto com o lead: recomeça (não vai de novo no próximo lead).
    if (body.register_journey) resetJourney();
    return { ok: true, ...out };
  } catch (e) {
    alertSendError(flow, 0, String((e && e.message) || e));
    return { ok: false, error: String((e && e.message) || e) };
  }
}

// Solicitacao de ambiente (trial): cria o lead na fase Inbound/"Solicitou ambiente"
// com o cadastro completo (empresa + pessoa + subdominio) na nota. A nota já leva
// tudo (é a fonte da verdade caso o cadastro estruturado falhe).
export async function sendTrial(data) {
  if (!isTrialEnabled()) return { skipped: true };
  const { company, cnpj, name, role, email, phone, subdomain, seats, product } = data;
  identifyVisitor(email, phone);
  const prod = product || 'Digitalk Plataforma';
  const note = [
    'SOLICITAÇÃO DE AMBIENTE (teste)',
    `Produto de interesse: ${prod}`,
    `Empresa: ${company}`,
    cnpj ? `CNPJ: ${cnpj}` : null,
    `Ambiente desejado: ${subdomain}.digitalk.com.br`,
    seats ? `Nº de atendentes/usuários: ${seats}` : null,
    `Responsável: ${name}${role ? ` (${role})` : ''}`,
    `E-mail: ${email}`,
    `WhatsApp: ${phone}`,
    'Origem: Landing Page - solicitação de ambiente',
  ].filter(Boolean).join('\n');
  const journey = getJourney({ canal: 'trial' });

  const body = {
    create_ticket: {
      name: `[${prod}] Solicitação de ambiente — ${company} · ${subdomain}.digitalk.com.br`,
      description: `Landing Page - Solicitação de ambiente (teste) · ${prod}`,
      phase_id: ID_PHASE_TRIAL,
    },
    register_customer: { id_table: ID_TABLE, nome: company, ativo: true },
    register_contact: [
      { contato: phone, tipo_contato: 'phone', canal_contato: 2 },
      { contato: email, tipo_contato: 'email', canal_contato: 4 },
    ],
    add_note: { message: note },
  };
  // Jornada do visitante: o api-core grava a linha no CRM e cria a atividade
  // "Jornada no site" no ticket (WF-4867) — não vai mais como texto na nota.
  if (journey) body.register_journey = journey;

  return postLead(body, 'trial');
}

// Lead do chatbot Digi (intro): a nota SEMPRE leva nome + e-mail + WhatsApp, para
// que o dado nunca se perca se o register_customer/register_contact falhar no
// consumer (schema do CRM configurável). O `note` opcional entra como observação.
export async function sendLead({ name, phone, email }, note) {
  if (!isLeadEnabled()) return { skipped: true };
  identifyVisitor(email, phone);

  const leadNote = [
    'DADOS DO LEAD (LP institucional · chatbot Digi)',
    `Nome: ${name}`,
    email ? `E-mail: ${email}` : null,
    phone ? `WhatsApp: ${phone}` : null,
    'Origem: Landing Page - Digitalk',
  ].filter(Boolean).join('\n');
  const journey = getJourney({ canal: 'formulario' });
  const base = note ? `${leadNote}\n\nObs.: ${note}` : leadNote;

  const body = {
    create_ticket: {
      name,
      description: 'Landing Page - Digitalk',
      phase_id: ID_PHASE,
    },
    register_customer: {
      id_table: ID_TABLE,
      nome: name,
      ativo: true,
      origem: 'Landing Page - Digitalk',
    },
    register_contact: [
      { contato: phone, tipo_contato: 'phone', canal_contato: 2 },
      { contato: email, tipo_contato: 'email', canal_contato: 4 },
    ],
    add_note: { message: base },
  };
  if (journey) body.register_journey = journey;

  return postLead(body, 'intro');
}
